import { Component } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { finalize } from 'rxjs/operators';

import { PasswordService } from '../../../account/password/password.service';
import { AlertService } from '../../../shared/alert/alert.service';

const MIN_LENGTH = 6;
const MAX_LENGTH = 50;

@Component({
  selector: 'app-change-password-dialog',
  standalone: false,
  templateUrl: './change-password-dialog.component.html',
  styleUrls: ['./change-password-dialog.component.scss'],
})
export class ChangePasswordDialogComponent {
  readonly minLength = MIN_LENGTH;
  readonly maxLength = MAX_LENGTH;

  form: FormGroup;
  isSaving = false;
  success = false;

  hideCurrent = true;
  hideNew = true;
  hideConfirm = true;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<ChangePasswordDialogComponent>,
    private passwordService: PasswordService,
    private alertService: AlertService
  ) {
    this.form = this.fb.group(
      {
        currentPassword: ['', [Validators.required]],
        newPassword: [
          '',
          [Validators.required, Validators.minLength(MIN_LENGTH), Validators.maxLength(MAX_LENGTH)],
        ],
        confirmPassword: ['', [Validators.required]],
      },
      { validators: [this.matchValidator, this.differentFromCurrentValidator] }
    );
  }

  /** New password and its confirmation must be equal. */
  private matchValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
    const newPassword = group.get('newPassword')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    if (!confirmPassword) {
      return null;
    }
    return newPassword === confirmPassword ? null : { doNotMatch: true };
  };

  /** New password must differ from the current one. */
  private differentFromCurrentValidator: ValidatorFn = (
    group: AbstractControl
  ): ValidationErrors | null => {
    const currentPassword = group.get('currentPassword')?.value;
    const newPassword = group.get('newPassword')?.value;
    if (!currentPassword || !newPassword) {
      return null;
    }
    return currentPassword === newPassword ? { sameAsCurrent: true } : null;
  };

  /** Strength level 1..5 for the strength meter (mirrors app-password-strength-bar logic). */
  get strengthLevel(): number {
    const s = this.measureStrength(this.form.get('newPassword')?.value || '');
    if (s <= 10) return 1;
    if (s <= 20) return 2;
    if (s <= 30) return 3;
    if (s <= 40) return 4;
    return 5;
  }

  private measureStrength(p: string): number {
    if (!p) {
      return 0;
    }
    let force = 0;
    const regex = /[$-/:-?{-~!"^_`[\]]/g; // "
    const flags = [/[a-z]+/.test(p), /[A-Z]+/.test(p), /[0-9]+/.test(p), regex.test(p)];
    const passedMatches = flags.filter(Boolean).length;

    force += 2 * p.length + (p.length >= 10 ? 1 : 0);
    force += passedMatches * 10;

    // penalty (short password)
    force = p.length <= 6 ? Math.min(force, 10) : force;

    // penalty (poor variety of characters)
    force = passedMatches === 1 ? Math.min(force, 10) : force;
    force = passedMatches === 2 ? Math.min(force, 20) : force;
    force = passedMatches === 3 ? Math.min(force, 40) : force;

    return force;
  }

  hasError(controlName: string, error: string): boolean {
    const control = this.form.get(controlName);
    return !!control && control.hasError(error) && (control.dirty || control.touched);
  }

  submit(): void {
    if (this.isSaving) {
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSaving = true;
    const { currentPassword, newPassword } = this.form.getRawValue();

    this.passwordService
      .save(newPassword, currentPassword)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        next: () => {
          this.success = true;
          this.alertService.success('', 'changePasswordDialog.messages.success');
          setTimeout(() => this.dialogRef.close(true), 1300);
        },
        error: (err) => {
          // Backend returns 400 (InvalidPasswordException) when the current password is wrong.
          if (err?.status === 400) {
            this.alertService.error('', 'changePasswordDialog.messages.wrongCurrent');
          } else {
            this.alertService.error('', 'changePasswordDialog.messages.error');
          }
        },
      });
  }

  cancel(): void {
    this.dialogRef.close(false);
  }
}
